import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  Alert as MuiAlert,
  Tooltip,
} from "@mui/material";
import {
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  ArrowBackIosNew as BackIcon,
  ViewModule as GridIcon,
  TableRows as TableIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  SwapVert as SwapVertIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
} from "@mui/icons-material";

import {
  getUserSearches,
  deleteSearch,
  sortSearches,
} from "../../services/apiService";

import ProfileTable from "../Profiles/ProfileTable";
import ProfileCard from "../Profiles/ProfileCard";
import ProfileDetails from "../Profiles/ProfileDetails";
import DeleteModal from "../Common/DeleteModal";
import CustomLoader from "../Common/CustomLoader";

import "./styles/SearchManagement.css";

const PRIMARY = "#2F4E8B";
const ROW_BG = "#F5F7F9";
const ROW_HOVER = "#E9EDF1";
const ROW_SELECTED = "#C9DAF7";

const SEARCHES_PER_PAGE = 5;
const PROFILES_PER_PAGE = 6;

export default function SearchManagement() {
  /* ---------------- state ---------------- */
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searches, setSearches] = useState([]);
  const [sortOrder, setSortOrder] = useState("desc");

  const [histPage, setHistPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);

  const [viewMode, setViewMode] = useState("card");
  const [profPage, setProfPage] = useState(1);

  const [delOpen, setDelOpen] = useState(false);
  const [delId, setDelId] = useState(null);

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const openSnack = (msg, sev = "success") =>
    setSnack({ open: true, msg, sev });
  const closeSnack = () => setSnack({ ...snack, open: false });

  const [selectedProfile, setSelectedProfile] = useState(null);
  const handleCloseDetails = () => setSelectedProfile(null);

  // Add ref for scrolling
  const containerRef = useRef(null);

  // Scroll to top helper
  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  /* --------------- fetch & sort via API --------------- */
  const loadSearches = async (order) => {
    setLoading(true);
    setError(null);
    try {
      const apiData = await sortSearches({ field: "createdAt", order });
      setSearches(apiData);
      setHistPage(1);
      setSelectedId(null);
    } catch {
      setError("Erreur lors de la récupération des recherches triées.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSearches(sortOrder);
  }, [sortOrder]);

  /* --------------- derived slices --------------- */
  const histTotal = Math.ceil(searches.length / SEARCHES_PER_PAGE);
  const histSlice = searches.slice(
    (histPage - 1) * SEARCHES_PER_PAGE,
    histPage * SEARCHES_PER_PAGE
  );

  const currentSearch = useMemo(
    () => searches.find((s) => s.id === selectedId) || null,
    [searches, selectedId]
  );

  const profTotal = currentSearch
    ? Math.ceil(currentSearch.results.length / PROFILES_PER_PAGE)
    : 1;
  const profSlice = currentSearch
    ? currentSearch.results.slice(
        (profPage - 1) * PROFILES_PER_PAGE,
        profPage * PROFILES_PER_PAGE
      )
    : [];

  useEffect(() => setProfPage(1), [selectedId]);

  /* --------------- delete --------------- */
  const confirmDelete = async () => {
    if (!delId) return;
    try {
      await deleteSearch(delId);
      setSearches((prev) => prev.filter((s) => s.id !== delId));
      if (delId === selectedId) setSelectedId(null);
      openSnack("Recherche supprimée", "success");
    } catch {
      openSnack("Échec de la suppression", "error");
    } finally {
      setDelOpen(false);
      setDelId(null);
    }
  };

  /* --------------- row component --------------- */
  const Row = ({ s }) => (
    <Box
      className={`sm-row ${selectedId === s.id ? "sm-selected" : ""}`}
      sx={{
        bgcolor: selectedId === s.id ? ROW_SELECTED : ROW_BG,
        "&:hover": { bgcolor: selectedId === s.id ? ROW_SELECTED : ROW_HOVER },
      }}
      onClick={() => setSelectedId(s.id)}
    >
      <Box className="sm-row-left">
        <Box display="flex" alignItems="center" gap={1}>
          <Typography className="sm-title">
            {s.filters.searchTerm || "Sans titre"}
          </Typography>
          <Typography className="sm-date">
            {new Date(s.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
        <Typography className="sm-filters">
          {[
            s.filters.localisation,
            s.filters.competences,
            s.filters.certifications,
            s.filters.formations,
            s.filters.interExp || s.filters.inter_exp
              ? `${s.filters.interExp || s.filters.inter_exp} ans`
              : "",
          ]
            .filter(Boolean)
            .join(" / ")}
        </Typography>
      </Box>

      <Box className="sm-actions" onClick={(e) => e.stopPropagation()}>
        <Tooltip title="Voir les résultats">
          <IconButton size="small" onClick={() => setSelectedId(s.id)}>
            <SearchIcon fontSize="small" />
            <Typography
              sx={{ ml: 0.5, fontSize: 13, fontWeight: 600, color: PRIMARY }}
            >
              Voir
            </Typography>
          </IconButton>
        </Tooltip>
        <Tooltip title="Supprimer la recherche">
          <IconButton
            size="small"
            onClick={() => {
              setDelId(s.id);
              setDelOpen(true);
            }}
          >
            <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  /* --------------- UI --------------- */
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CustomLoader />
      </Box>
    );
  }
  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box className="search-management-container" ref={containerRef}>
      {selectedProfile && (
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <IconButton onClick={handleCloseDetails}>
              <ArrowBackIosIcon sx={{ color: PRIMARY }} />
            </IconButton>
            <Typography variant="h6" fontWeight={700}>
              Détails du profil
            </Typography>
          </Box>
          <ProfileDetails
            profile={selectedProfile}
            onCloseDetails={handleCloseDetails}
          />
        </Box>
      )}

      {!selectedProfile && (
        <>
          <Box
            className="sm-topbar"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box display="flex" alignItems="center" gap={1}>
              <BackIcon
                sx={{ cursor: "pointer", color: PRIMARY }}
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent("navigateToComponent", {
                      detail: { component: "profiles" },
                    })
                  )
                }
              />
              <Typography variant="h5" fontWeight={700}>
                Historique
              </Typography>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              gap={0.5}
              sx={{ cursor: "pointer" }}
              onClick={() =>
                setSortOrder((o) => (o === "desc" ? "asc" : "desc"))
              }
            >
          <Tooltip title="Trier par date">
                <ArrowUpwardIcon
                  sx={{
                    fontSize: 20,
                    color: sortOrder === "asc" ? "#e10054" : "#4F5565",
                  }}
                />
              </Tooltip>
              <Tooltip title="Trier par date">
                <ArrowDownwardIcon
                  sx={{
                    fontSize: 20,
                    color: sortOrder === "desc" ? "#e10054" : "#4F5565",
                    mt: "-2px",
                  }}
                />
              </Tooltip>
              <Typography variant="body2" sx={{ fontWeight: 600, color: PRIMARY }}>
                ({sortOrder === "desc" ? "▼" : "▲"})
              </Typography>
            </Box>
          </Box>

          {histSlice.map((s) => (
            <Row key={s.id} s={s} />
          ))}

          {histTotal > 1 && (
            <Box className="sm-pagination">
              <IconButton
                onClick={() => {
                  setHistPage((p) => p - 1);
                  scrollToTop();
                }}
                disabled={histPage === 1}
              >
                <ArrowBackIosIcon />
              </IconButton>
              <Box className="sm-page-bubble">{histPage}</Box>
              <IconButton
                onClick={() => {
                  setHistPage((p) => p + 1);
                  scrollToTop();
                }}
                disabled={histPage === histTotal}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </Box>
          )}

          {!currentSearch && (
            <Typography sx={{ mt: 3, fontStyle: "italic", color: "#6e7a8a" }}>
              Sélectionnez une recherche pour en voir les résultats.
            </Typography>
          )}

          {currentSearch && (
            <Box sx={{ mt: 4 }}>
              <Box className="sm-res-bar">
                <Typography fontWeight={700}>Résultats de la recherche</Typography>
                <Box className="sm-view-btn">
                  <Tooltip title="Mode cartes">
                    <IconButton
                      data-active={viewMode === "card"}
                      onClick={() => setViewMode("card")}
                    >
                      <GridIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Mode tableau">
                    <IconButton
                      data-active={viewMode === "table"}
                      onClick={() => setViewMode("table")}
                    >
                      <TableIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {viewMode === "card" ? (
                <Box className="sm-card-grid">
                  {profSlice.map((p) => (
                    <ProfileCard
                      key={p.id}
                      profile={p}
                      onClick={() => setSelectedProfile(p)}
                    />
                  ))}
                </Box>
              ) : (
                <ProfileTable rows={profSlice} onSelect={(p) => setSelectedProfile(p)} />
              )}

              {profTotal > 1 && (
                <Box className="sm-pagination">
                  <IconButton
                    onClick={() => {
                      setProfPage((p) => p - 1);
                      scrollToTop();
                    }}
                    disabled={profPage === 1}
                  >
                    <ArrowBackIosIcon />
                  </IconButton>
                  <Box className="sm-page-bubble">{profPage}</Box>
                  <IconButton
                    onClick={() => {
                      setProfPage((p) => p + 1);
                      scrollToTop();
                    }}
                    disabled={profPage === profTotal}
                  >
                    <ArrowForwardIosIcon />
                  </IconButton>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      <DeleteModal
        open={delOpen}
        onClose={() => setDelOpen(false)}
        onConfirm={confirmDelete}
        message="Êtes‑vous sûr de vouloir supprimer cette recherche ?"
      />

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MuiAlert onClose={closeSnack} severity={snack.sev} sx={{ width: "100%" }}>
          {snack.msg}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
